import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown, Heading, Loader2, Save } from "lucide-react";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { FormSection, TextArea, TextField } from "./FormShell";
import { revalidatePublic } from "../utils/revalidatePublic";

// Manages the body-intro text on the public /executive-leadership page
// — the "Meet the Team" heading + accent word + paragraph description
// that sit between the hero and the profile grid.
//
// Writes to the SAME pageSettings/executive-leadership doc that
// PublicPageSettings uses (hero + SEO). Uses merge writes so the two
// components don't clobber each other's fields.

const SETTINGS_COLLECTION = "pageSettings";
const SETTINGS_KEY = "executive-leadership";

const DEFAULTS = {
  bodyHeading: "Meet the",
  bodyHeadingAccent: "Team",
  bodyDescription:
    "The people leading LPC's practice areas — commercial leasing, investment sales, research and valuation, and hospitality — bring decades of combined market experience to every mandate.",
};

export default function LeadershipBodySettings() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [initial, setInitial] = useState(DEFAULTS);

  useEffect(() => {
    let active = true;
    (async () => {
      const data = await getDocData(SETTINGS_COLLECTION, SETTINGS_KEY);
      if (!active) return;
      const next = {
        bodyHeading: data?.bodyHeading ?? DEFAULTS.bodyHeading,
        bodyHeadingAccent:
          data?.bodyHeadingAccent ?? DEFAULTS.bodyHeadingAccent,
        bodyDescription:
          data?.bodyDescription ?? DEFAULTS.bodyDescription,
      };
      setForm(next);
      setInitial(next);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const isDirty = Object.keys(DEFAULTS).some(
    (k) => (form[k] ?? "") !== (initial[k] ?? ""),
  );

  const update = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Read-then-merge so we don't clobber the hero/SEO fields that
      // PublicPageSettings owns on the same doc.
      const existing =
        (await getDocData(SETTINGS_COLLECTION, SETTINGS_KEY)) || {};
      await setDocData(SETTINGS_COLLECTION, SETTINGS_KEY, {
        ...existing,
        bodyHeading: form.bodyHeading.trim(),
        bodyHeadingAccent: form.bodyHeadingAccent.trim(),
        bodyDescription: form.bodyDescription,
        updatedAt: new Date().toISOString(),
      });
      revalidatePublic(["/executive-leadership", "/aboutUs"]);
      setInitial(form);
      toast.success("Section text saved.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-4 bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Heading className="w-4 h-4 text-leechiuBlue" />
          <div>
            <h3 className="text-sm font-semibold text-leechiuBlue m-0">
              Section text
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              The "{loading ? "…" : `${initial.bodyHeading} ${initial.bodyHeadingAccent}`.trim()}" block above the profile grid.
            </p>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div className="border-t border-gray-100 p-5">
          <FormSection>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
                  <TextField
                    label="Heading"
                    value={form.bodyHeading}
                    onChange={update("bodyHeading")}
                    placeholder={DEFAULTS.bodyHeading}
                    hint='The plain part of the heading. Example: "Meet the"'
                  />
                  <TextField
                    label="Accent word"
                    value={form.bodyHeadingAccent}
                    onChange={update("bodyHeadingAccent")}
                    placeholder={DEFAULTS.bodyHeadingAccent}
                    hint='The trailing word rendered in orange. Example: "Team". Leave blank for no accent.'
                  />
                </div>

                <div className="text-xs text-gray-500 mt-1">
                  Preview:{" "}
                  <span className="font-serif text-leechiuBlue font-semibold">
                    {form.bodyHeading}
                    {form.bodyHeadingAccent ? (
                      <>
                        {" "}
                        <span className="text-leechiuOrange">
                          {form.bodyHeadingAccent}
                        </span>
                      </>
                    ) : null}
                  </span>
                </div>

                <TextArea
                  label="Description"
                  rows={4}
                  value={form.bodyDescription}
                  onChange={update("bodyDescription")}
                  placeholder={DEFAULTS.bodyDescription}
                  hint="Paragraph below the heading. Line breaks are preserved."
                />

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setForm(initial)}
                    disabled={!isDirty || saving}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!isDirty || saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save
                  </button>
                </div>
              </>
            )}
          </FormSection>
        </div>
      ) : null}
    </section>
  );
}
